import { useState } from 'react'
import { useNavigate } from 'react-router'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { RecordFactory } from '../../models/RecordFactory'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { ComponentData } from '../../utils/typing'
import { getEditionID } from '../../utils/users'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { TextArea } from '../common/fields/TextArea'
import { WorkflowPage } from '../workflows/WorkflowPage'

const viewWillAppear = async (base: PocketComponent) => {
    return {
        edition: await RecordFactory.getInstance().loadEdition(getEditionID(base.me)),
    }
}

const render = ({ data }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const originalUser = PSApplication.me()
    const { advanceOnboardingState } = useOnboardingContext()
    const [user, setUser] = useState(
        originalUser.clone({
            bio: originalUser.bio || data.edition?.bio || '',
        })
    )
    const [isLoading, setIsLoading] = useState(false)
    const { logAndDisplayError } = useErrorHandler()
    const navigate = useNavigate()
    usePageTitle('Profile')
    const { goBack } = useHistoryTrackerContext()

    const [photo, setPhoto] = useState<undefined | string | File>(user.image?.url)

    const onNextHandler = async () => {
        setIsLoading(true)

        const result = await RecordFactory.getInstance().sendUser(
            user,
            typeof photo === 'string' ? undefined : photo
        )

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        await advanceOnboardingState()
        setIsLoading(false)
    }

    return (
        <WorkflowPage
            onClose={() => navigate('/')}
            onBack={() => goBack()}
            backButtonTitle={'Back'}
            footerContent={
                <div className="flex justify-end">
                    <Button intent="primary" onClick={onNextHandler} isLoading={isLoading}>
                        Finish
                    </Button>
                </div>
            }
        >
            <Heading size="xl" className="mt-[5px]">
                Profile
            </Heading>
            <Paragraph className="mt-4 max-w-[580px] text-center text-gray-600">
                This photo will represent you and your business. You can make it a logo or of
                yourself!
            </Paragraph>

            <WorkflowPage.Card className="mt-12 flex flex-col gap-y-8">
                <WorkflowPage.PhotoPicker
                    selectedPhoto={
                        photo && typeof photo === 'string'
                            ? { type: 'url', url: photo }
                            : photo && typeof photo !== 'string'
                              ? { type: 'file', file: photo }
                              : null
                    }
                    onSelectPhoto={(photo) => {
                        setPhoto(photo)
                    }}
                    onClearPhoto={() => {
                        setPhoto(undefined)
                    }}
                    size="medium"
                />
                <WorkflowPage.IconSection
                    title="Business description"
                    icon="note.large"
                    className="z-20"
                >
                    <TextArea
                        value={user.bio}
                        onClear={() => setUser(user.clone({ bio: '' }))}
                        onChange={(e) => setUser(user.clone({ bio: e.target.value }))}
                        isAutoResizing
                        placeholder="Describe your business in a few sentences"
                    />
                </WorkflowPage.IconSection>
            </WorkflowPage.Card>
        </WorkflowPage>
    )
}

export const SetupOnboardingProfilePage = usePSComponent(render, viewWillAppear)
